import { useMemo } from "react";
import { useCustom } from "@refinedev/core";

/**
 * De financiële keuzelijsten bij een prijsversie: grootboekrekening, kostenplaats en projectcode.
 *
 * <p>Ze staan bij een prijsversie en niet alleen bij het tarief: wie de grootboekrekening op het tarief
 * wijzigt verandert anders ook waar prijzen van jaren geleden onder vielen, en dat vervalst oude
 * boekingen. Leeg mag; dan geldt wat er op het tarief staat.
 *
 * <p>Ze staan hier apart en niet in één van de twee tariefschermen, omdat beide ze nodig hebben. Een
 * tweede kopie zou vroeg of laat een andere lijst tonen of een andere leeg-betekenis kiezen dan de
 * eerste, en dan hangt het van het scherm af waar een prijs op geboekt wordt.
 */

export interface FinancieleOptie {
  id: number;
  label: string;
}

export interface FinancieleOpties {
  grootboekRekeningen: FinancieleOptie[];
  kostenPlaatsen: FinancieleOptie[];
  projectCodes: FinancieleOptie[];
}

export function useFinancieleOpties(): FinancieleOpties {
  const { data } = useCustom<{
    grootboekRekeningen?: FinancieleOptie[];
    kostenPlaatsen?: FinancieleOptie[];
    projectCodes?: FinancieleOptie[];
  }>({ url: "producten/opties", method: "get", queryOptions: { retry: false } });
  return useMemo(() => ({
    grootboekRekeningen: data?.data?.grootboekRekeningen ?? [],
    kostenPlaatsen: data?.data?.kostenPlaatsen ?? [],
    projectCodes: data?.data?.projectCodes ?? [],
  }), [data]);
}

/** Het label bij een id, of een streepje als er niets gekozen is of de lijst hem niet kent. */
export function labelVan(lijst: FinancieleOptie[], id: number | null): string {
  if (id == null) {
    return "—";
  }
  return lijst.find((o) => o.id === id)?.label ?? String(id);
}

/** Een keuzelijst voor de invoer: de lege keuze voorop, daarna de opties. */
export function keuzes(lijst: FinancieleOptie[], leegLabel: string): { value: string; label: string }[] {
  return [{ value: "", label: leegLabel }, ...lijst.map((o) => ({ value: String(o.id), label: o.label }))];
}

/** Een gekozen id als getal, of leeg als er niets gekozen is. */
export function idOf(waarde: string): number | null {
  return waarde === "" ? null : Number(waarde);
}
